import { useRef, useEffect, useState, useCallback } from 'react';

interface UseScrollDetectionOptions {
  /**
   * Threshold for scroll detection (in pixels)
   * @default 0
   */
  threshold?: number;
  /**
   * Debounce delay for scroll events (in milliseconds)
   * @default 0
   */
  debounceMs?: number;
  /**
   * Whether to track horizontal scrolling
   * @default false
   */
  trackHorizontal?: boolean;
  /**
   * Whether to track vertical scrolling
   * @default true
   */
  trackVertical?: boolean;
}

interface ScrollState {
  /**
   * Whether the element has been scrolled
   */
  hasScrolled: boolean;
  /**
   * Current scroll position from top
   */
  scrollTop: number;
  /**
   * Current scroll position from left
   */
  scrollLeft: number;
  /**
   * Whether the element is at the top
   */
  isAtTop: boolean;
  /**
   * Whether the element is at the bottom
   */
  isAtBottom: boolean;
  /**
   * Whether the element is at the left edge
   */
  isAtLeft: boolean;
  /**
   * Whether the element is at the right edge
   */
  isAtRight: boolean;
}

export function useScrollDetection<T extends HTMLElement = HTMLElement>(
  options: UseScrollDetectionOptions = {}
) {
  const {
    threshold = 0,
    debounceMs = 0,
    trackHorizontal = false,
    trackVertical = true,
  } = options;

  const ref = useRef<T>(null);
  const timeoutRef = useRef<NodeJS.Timeout>(null);
  const [scrollState, setScrollState] = useState<ScrollState>({
    hasScrolled: false,
    scrollTop: 0,
    scrollLeft: 0,
    isAtTop: true,
    isAtBottom: false,
    isAtLeft: true,
    isAtRight: false,
  });

  const updateScrollState = useCallback(() => {
    if (!ref.current) return;

    const element = ref.current;
    const scrollTop = element.scrollTop;
    const scrollLeft = element.scrollLeft;
    const scrollHeight = element.scrollHeight;
    const scrollWidth = element.scrollWidth;
    const clientHeight = element.clientHeight;
    const clientWidth = element.clientWidth;

    const hasScrolledVertically = trackVertical && scrollTop > threshold;
    const hasScrolledHorizontally = trackHorizontal && scrollLeft > threshold;
    const hasScrolled = hasScrolledVertically || hasScrolledHorizontally;

    const isAtTop = !trackVertical || scrollTop <= threshold;
    const isAtBottom =
      !trackVertical || scrollTop + clientHeight >= scrollHeight - threshold;
    const isAtLeft = !trackHorizontal || scrollLeft <= threshold;
    const isAtRight =
      !trackHorizontal || scrollLeft + clientWidth >= scrollWidth - threshold;

    setScrollState({
      hasScrolled,
      scrollTop,
      scrollLeft,
      isAtTop,
      isAtBottom,
      isAtLeft,
      isAtRight,
    });
  }, [threshold, trackHorizontal, trackVertical]);

  const debouncedUpdateScrollState = useCallback(() => {
    if (debounceMs > 0) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(updateScrollState, debounceMs);
    } else {
      updateScrollState();
    }
  }, [updateScrollState, debounceMs]);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    updateScrollState();

    element.addEventListener('scroll', debouncedUpdateScrollState, {
      passive: true,
    });

    const resizeObserver = new ResizeObserver(() => {
      updateScrollState();
    });
    resizeObserver.observe(element);

    return () => {
      element.removeEventListener('scroll', debouncedUpdateScrollState);
      resizeObserver.disconnect();
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [debouncedUpdateScrollState, updateScrollState]);

  return {
    ref,
    ...scrollState,
  };
}
